import { useMemo } from "react"
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts"

// Generic multi-series line chart. Supports an optional secondary
// (right-side) y-axis per series, an optional horizontal reference line
// (e.g. SLA goal), and per-series dashed strokes. The canonical two-
// series variant lives in `dual-line-chart.tsx` as a thin wrapper.

export type LineChartSeries = {
  id: string
  label: string
  data: (number | null)[]
  color: string
  /** When "right" the series renders against a secondary y-axis. */
  yAxisId?: "left" | "right"
  /** Right-axis formatter override (uses formatY when omitted). */
  formatValue?: (n: number) => string
  strokeDasharray?: string
}

type Props = {
  dates: string[]
  series: LineChartSeries[]
  height?: number
  formatY?: (n: number) => string
  formatYRight?: (n: number) => string
  formatTooltip?: (n: number, seriesId: string) => string
  /** Horizontal goal line on the left axis. */
  referenceY?: number | null
  referenceLabel?: string
  emptyMessage?: string
}

export function LineChart({
  dates,
  series,
  height = 240,
  formatY,
  formatYRight,
  formatTooltip,
  referenceY = null,
  referenceLabel,
  emptyMessage = "No data in this window yet.",
}: Props) {
  const data = useMemo(() => {
    return dates.map((d, i) => {
      const row: Record<string, string | number | null> = { date: d }
      for (const s of series) {
        row[s.id] = s.data[i] ?? null
      }
      return row
    })
  }, [dates, series])

  const hasAny = data.some((row) => series.some((s) => row[s.id] != null))
  if (!hasAny) {
    return (
      <div className="grid place-items-center text-sm text-app-muted" style={{ height }}>
        {emptyMessage}
      </div>
    )
  }

  const hasRightAxis = series.some((s) => s.yAxisId === "right")

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ReLineChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="date" stroke="#888" tick={{ fontSize: 11 }} tickFormatter={fmtShortDate} />
        <YAxis
          yAxisId="left"
          stroke="#888"
          tick={{ fontSize: 11 }}
          tickFormatter={(v: number) => (formatY ? formatY(v) : String(v))}
        />
        {hasRightAxis && (
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#888"
            tick={{ fontSize: 11 }}
            tickFormatter={(v: number) => (formatYRight ? formatYRight(v) : String(v))}
          />
        )}
        <Tooltip
          contentStyle={{ background: "#1f1f23", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
          labelStyle={{ color: "#aaa" }}
          itemStyle={{ color: "#fff" }}
          formatter={(v, _name, payload) => {
            if (v == null) return "—"
            const n = typeof v === "number" ? v : Number(v)
            const seriesId = String(payload?.dataKey ?? "")
            if (formatTooltip) return formatTooltip(n, seriesId)
            const s = series.find((x) => x.id === seriesId)
            if (s?.yAxisId === "right" && formatYRight) return formatYRight(n)
            return formatY ? formatY(n) : String(n)
          }}
          labelFormatter={(d) => fmtShortDate(String(d))}
        />
        {referenceY != null && (
          <ReferenceLine
            y={referenceY}
            yAxisId="left"
            stroke="#888"
            strokeDasharray="4 4"
            label={
              referenceLabel
                ? { value: referenceLabel, fill: "#aaa", fontSize: 10, position: "insideTopRight" }
                : undefined
            }
          />
        )}
        {series.map((s) => (
          <Line
            key={s.id}
            yAxisId={s.yAxisId ?? "left"}
            type="monotone"
            dataKey={s.id}
            name={s.label}
            stroke={s.color}
            strokeWidth={2}
            strokeDasharray={s.strokeDasharray}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        ))}
      </ReLineChart>
    </ResponsiveContainer>
  )
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}
